//@ts-nocheck
//@ts-ignore
import { differenceInCalendarDays as diffInDays } from "date-fns";
import { DEGREE_DATA, SCHEDULE_DATA } from "./constants";
import {
  average,
  chunkArray,
  pipeFunc,
  standardDeviation,
} from "../../utils/functions";
import { interpretTutorData } from "./tutor-fns";

function combineSearchArrays(initialArr, newArr, pageLimit) {
  let result =
    initialArr.length < pageLimit
      ? initialArr.concat(newArr.splice(0, pageLimit - initialArr.length))
      : initialArr;

  return result;
}

const rankFx = {
  class: 6,
  subject: 6,
  curriculum: 6,
  purpose: 7,
  specialNeed: 6,
  video: 8,
  badge: 7,
  students: 5,
  lessons: 5,
  price: 12,
  headline: 10,
  description: 7,
  experience: 5,
  education: 5,
  distance: 4,
  calendar: 4,
  decline: -5,
};

function getMaxValue(tutors, key) {
  let valueArr = tutors.map((item) => item[key]);
  return Math.max(...valueArr);
}

function getPriceScore(tutorDataOptions, tutorData) {
  let priceArr = tutorDataOptions.map(({ price }) => price.amount);
  let mean = average(priceArr);
  let deviation = standardDeviation(priceArr);
  let zScore = (tutorData.price.amount - mean) / deviation;
  let absZScore = Math.abs(zScore);
  if (absZScore <= 0.25) return rankFx.price;
  if (absZScore > 0.25 && absZScore <= 0.5) return rankFx.price / 2;
  if (absZScore > 0.5 && absZScore <= 1) return rankFx.price / 3;
  if (absZScore > 1 && absZScore <= 1.5) return rankFx.price / 4;
  if (absZScore > 1.5 && absZScore <= 2) return rankFx.price / 6;
  return 0;
}

export function getTeachingExperience(tutorData) {
  let teachingWorkHistory = tutorData.workHistory.filter(
    (workHistory) => workHistory.isTeachingRole,
  );

  const workReducer = (accumulator, currentValue) => {
    if (currentValue.isCurrent) {
      let today = new Date();
      let diffInYears = today.getFullYear() - parseInt(currentValue.startYear);
      return accumulator + diffInYears;
    }

    if (currentValue.endYear === currentValue.startYear) {
      let diffInYears =
        parseInt(currentValue.endYear) - parseInt(currentValue.startYear) + 1;
      return accumulator + diffInYears;
    }

    let diffInYears =
      parseInt(currentValue.endYear) - parseInt(currentValue.startYear);
    return accumulator + diffInYears;
  };

  return teachingWorkHistory.reduce(workReducer, 0);
}

function getTeachingExperienceScore(tutorData) {
  let experience = getTeachingExperience(tutorData);
  if (experience > -1 && experience <= rankFx.experience) return experience;
  if (experience > rankFx.experience) return rankFx.experience;
}

export function calculateTutorAge(tutorData) {
  let birthday = new Date(tutorData.dateOfBirth);
  let milliSecDiff = Date.now() - birthday.getTime();
  let ageDate = new Date(milliSecDiff);

  return Math.abs(ageDate.getUTCFullYear() - 1970);
}

function getEducationScore(tutorData) {
  let highestEducation = tutorData.education[0];
  let degree = DEGREE_DATA.find(
    (data) => data.abbrev === highestEducation.degree,
  );
  if (degree !== undefined) return degree.rank;
  return 1;
}
function getRelevantScoreExcludingPrice(
  tutorOptions,
  tutorDataOptions = [],
  searchObj,
) {
  let maxStudents = getMaxValue(tutorOptions, "students");
  let maxLessons = getMaxValue(tutorOptions, "lessonsTaught");

  let tutorOptionsArray = tutorOptions.map((tutor) => {
    let tutorData = tutorDataOptions.find(
      (data) => data.userId === tutor.userId,
    );
    let relevanceScore = 0;

    function matchCount(searchObjKey = "", tutorObjKey = "", weight) {
      let matchedCount = 0;
      if (searchObj[searchObjKey]?.length) {
        let matchedItems = searchObj[searchObjKey].filter((item) =>
          (tutor[tutorObjKey] || []).includes(item),
        );
        matchedCount =
          (matchedItems.length / searchObj[searchObjKey].length) * weight;
      }
      return matchedCount;
    }

    function getWordMatchCount(tutorObjKey = "") {
      let matchCount = 0;
      let mainText = tutor.subject[tutorObjKey].slice(0, 150).split(" ");
      let searchString =
        `${searchObj.purposes?.join(" ")} ${searchObj.searchSubject} ${
          tutorData.education[0].course
        }` || "";

      let searchStringArray = [...new Set(searchString.split(" "))];

      searchStringArray.forEach((string) =>
        mainText.forEach((text) => {
          if (text.includes(string)) matchCount++;
        }),
      );

      if (matchCount >= 5) return rankFx[tutorObjKey];
      if (matchCount === 4) return rankFx[tutorObjKey] / 1.5;
      if (matchCount === 3) return rankFx[tutorObjKey] / 2;
      if (matchCount === 2) return rankFx[tutorObjKey] / 2.5;
      if (matchCount === 1) return rankFx[tutorObjKey] / 3;
      return 0;
    }

    function getDistanceRank() {
      if (tutor.distance <= 3) return rankFx.distance;
      if (tutor.distance > 3 && tutor.distance <= 6) return rankFx.distance - 1;
      if (tutor.distance > 6 && tutor.distance <= 9) return rankFx.distance - 2;
      if (tutor.distance > 9 && tutor.distance <= 12)
        return rankFx.distance - 3;
      return 0;
    }

    function getDeclinedRank() {
      if (tutor.requestsDeclined === 2) return rankFx.decline;
      return 0;
    }

    function getCalendarUpdateRank() {
      const today = new Date();
      const lastUpdate = new Date(tutor.lastCalendarUpdate);
      const difference = diffInDays(today, lastUpdate);
      if (difference <= 14) return rankFx.calendar;
      if (difference > 14 && difference <= 28) return rankFx.calendar - 2;
      return 0;
    }

    let classRank = matchCount("class", "classesTaught", rankFx.class);
    let subjectRank = matchCount("subjectGroup", "subjectList", rankFx.subject);
    let curriculumRank = matchCount(
      "curriculum",
      "curriculum",
      rankFx.curriculum,
    );
    let purposeRank = matchCount("purposes", "examsExperience", rankFx.purpose);
    let specialNeedsRank = matchCount(
      "specialNeeds",
      "specialNeedExpertise",
      rankFx.specialNeed,
    );
    let distanceRank = getDistanceRank();
    let calendarRank = getCalendarUpdateRank();
    let declineRank = getDeclinedRank();

    let requestRank =
      classRank +
      subjectRank +
      curriculumRank +
      purposeRank +
      specialNeedsRank +
      distanceRank +
      calendarRank +
      declineRank;

    if (tutor.videoIntro) relevanceScore = relevanceScore + rankFx.video;
    if (tutorData.badges.length > 0)
      relevanceScore = relevanceScore + rankFx.badge;
    if (maxStudents > 0) {
      relevanceScore =
        relevanceScore + (tutor.students / maxStudents) * rankFx.students;
    }
    if (maxLessons > 0) {
      relevanceScore =
        relevanceScore + (tutor.lessonsTaught / maxLessons) * rankFx.lessons;
    }

    if (tutor.workHistory.length > 0) {
      relevanceScore = relevanceScore + getTeachingExperienceScore(tutor);
    }

    if (tutor.education.length > 0) {
      relevanceScore = relevanceScore + getEducationScore(tutorData);
    }

    if (tutor.subject.headline) {
      relevanceScore = relevanceScore + getWordMatchCount("headline");
    }

    if (tutor.subject.description) {
      relevanceScore = relevanceScore + getWordMatchCount("description");
    }
    if (tutor.workHistory.map((o) => o.isTeachingProfile).includes(true)) {
    }
    return {
      ...tutor,
      rank: relevanceScore + requestRank,
      lessonFee: tutorData.price.amount,
      experience: getTeachingExperience(tutor),
      isIndemand: tutor.activeBookings.length > 1,
      age: calculateTutorAge(tutor),
      eduDegrees: tutor.education.map((obj) => obj.degree),
      eduGrades: Array.from(new Set(tutor.education.map((obj) => obj.grade))),
      eduCountries: Array.from(
        new Set(tutor.education.map((obj) => obj.country)),
      ),
    };
  });

  return tutorOptionsArray;
}
function getRelevantScorePriceOnly(
  tutorOptions: any,
  tutorDataOptions: any,
  searchObj?: any,
) {
  let tutorOptionsArray = tutorOptions.map((tutor) => {
    let tutorData = tutorDataOptions.find(
      (data) => data.userId === tutor.userId,
    );
    let relevanceScore = 0;

    if (tutorData.price.amount) {
      relevanceScore =
        relevanceScore + getPriceScore(tutorDataOptions, tutorData);
    }
    return {
      ...tutor,
      rank: (tutor.rank || 0) + relevanceScore,
      lessonFee: tutorData.price.amount,
    };
  });

  return tutorOptionsArray;
}
export const getRelevanceScore = (
  tutorOptions = [],
  tutorDataOptions = [],
  searchObj,
) => {
  let result = getRelevantScoreExcludingPrice(
    tutorOptions,
    tutorDataOptions,
    searchObj,
  );
  return getRelevantScorePriceOnly(result, tutorDataOptions);
};
export const oldGetRelevanceScore = (
  tutorOptions = [],
  // requestData,
  tutorDataOptions = [],
  searchObj,
) => {
  // let tutorDataOptions = tutorOptions.map((tutor) => {
  //   let data = interpretTutorData(tutor, requestData, searchObj);
  //   return { ...data, ["userId"]: tutor.userId };
  // });

  let maxStudents = getMaxValue(tutorOptions, "students");
  let maxLessons = getMaxValue(tutorOptions, "lessonsTaught");

  let tutorOptionsArray = tutorOptions.map((tutor) => {
    let tutorData = tutorDataOptions.find(
      (data) => data.userId === tutor.userId,
    );
    let relevanceScore = 0;

    function matchCount(searchObjKey = "", tutorObjKey = "", weight) {
      let matchedCount = 0;
      if (searchObj[searchObjKey].length) {
        let matchedItems = searchObj[searchObjKey].filter((item) =>
          (tutor[tutorObjKey] || []).includes(item),
        );
        matchedCount =
          (matchedItems.length / searchObj[searchObjKey].length) * weight;
      }
      return matchedCount;
    }

    function getWordMatchCount(tutorObjKey = "") {
      let matchCount = 0;
      let mainText = tutor.subject[tutorObjKey].slice(0, 150).split(" ");
      let searchString = `${searchObj.purposes.join(" ")} ${
        searchObj.searchSubject
      } ${tutorData.education[0].course}`;

      let searchStringArray = [...new Set(searchString.split(" "))];

      searchStringArray.forEach((string) =>
        mainText.forEach((text) => {
          if (text.includes(string)) matchCount++;
        }),
      );

      if (matchCount >= 5) return rankFx[tutorObjKey];
      if (matchCount === 4) return rankFx[tutorObjKey] / 1.5;
      if (matchCount === 3) return rankFx[tutorObjKey] / 2;
      if (matchCount === 2) return rankFx[tutorObjKey] / 2.5;
      if (matchCount === 1) return rankFx[tutorObjKey] / 3;
      return 0;
    }

    function getDistanceRank() {
      if (tutor.distance <= 3) return rankFx.distance;
      if (tutor.distance > 3 && tutor.distance <= 6) return rankFx.distance - 1;
      if (tutor.distance > 6 && tutor.distance <= 9) return rankFx.distance - 2;
      if (tutor.distance > 9 && tutor.distance <= 12)
        return rankFx.distance - 3;
      return 0;
    }

    function getDeclinedRank() {
      if (tutor.requestsDeclined === 2) return rankFx.decline;
      return 0;
    }

    function getCalendarUpdateRank() {
      const today = new Date();
      const lastUpdate = new Date(tutor.lastCalendarUpdate);
      const difference = diffInDays(today, lastUpdate);
      if (difference <= 14) return rankFx.calendar;
      if (difference > 14 && difference <= 28) return rankFx.calendar - 2;
      return 0;
    }

    let classRank = matchCount("class", "classesTaught", rankFx.class);
    let subjectRank = matchCount("subjectGroup", "subjectList", rankFx.subject);
    let curriculumRank = matchCount(
      "curriculum",
      "curriculum",
      rankFx.curriculum,
    );
    let purposeRank = matchCount("purposes", "examsExperience", rankFx.purpose);
    let specialNeedsRank = matchCount(
      "specialNeeds",
      "specialNeedExpertise",
      rankFx.specialNeed,
    );
    let distanceRank = getDistanceRank();
    let calendarRank = getCalendarUpdateRank();
    let declineRank = getDeclinedRank();

    let requestRank =
      classRank +
      subjectRank +
      curriculumRank +
      purposeRank +
      specialNeedsRank +
      distanceRank +
      calendarRank +
      declineRank;

    if (tutor.videoIntro) relevanceScore = relevanceScore + rankFx.video;
    if (tutorData.badges.length > 0)
      relevanceScore = relevanceScore + rankFx.badge;
    if (maxStudents > 0) {
      relevanceScore =
        relevanceScore + (tutor.students / maxStudents) * rankFx.students;
    }
    if (maxLessons > 0) {
      relevanceScore =
        relevanceScore + (tutor.lessonsTaught / maxLessons) * rankFx.lessons;
    }
    if (tutorData.price.amount) {
      relevanceScore =
        relevanceScore + getPriceScore(tutorDataOptions, tutorData);
    }
    if (tutor.workHistory.length > 0) {
      relevanceScore = relevanceScore + getTeachingExperienceScore(tutor);
    }

    if (tutor.education.length > 0) {
      relevanceScore = relevanceScore + getEducationScore(tutorData);
    }

    if (tutor.subject.headline) {
      relevanceScore = relevanceScore + getWordMatchCount("headline");
    }

    if (tutor.subject.description) {
      relevanceScore = relevanceScore + getWordMatchCount("description");
    }
    if (tutor.workHistory.map((o) => o.isTeachingProfile).includes(true)) {
    }
    return {
      ...tutor,
      rank: relevanceScore + requestRank,
      lessonFee: tutorData.price.amount,
      experience: getTeachingExperience(tutor),
      isIndemand: tutor.activeBookings.length > 1,
      age: calculateTutorAge(tutor),
      eduDegrees: tutor.education.map((obj) => obj.degree),
      eduGrades: Array.from(new Set(tutor.education.map((obj) => obj.grade))),
      eduCountries: Array.from(
        new Set(tutor.education.map((obj) => obj.country)),
      ),
    };
  });

  return tutorOptionsArray;
};

const sortByRelevance = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => b.rank - a.rank);
};

const sortByLeastPrice = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => a.lessonFee - b.lessonFee);
};

const sortByHighestPrice = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => b.lessonFee - a.lessonFee);
};

const sortByDistance = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => a.distance - b.distance);
};

const sortByRatings = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => b.rating - a.rating);
};

const sortByRatingCount = (tutorOptions = []) => {
  return tutorOptions.sort((a, b) => b.ratingCount - a.ratingCount);
};

const sortByNewtutors = (tutorOptions = []) => {
  console.log(tutorOptions);
  return tutorOptions.filter((o) => o.isNewTutor);
};

const sortingFunctions = {
  Recommended: sortByRelevance,
  "Lowest price first": sortByLeastPrice,
  "Highest price first": sortByHighestPrice,
  "Number of reviews": sortByRatingCount,
  "Highest ratings": sortByRatings,
  "Closest distance": sortByDistance,
  "New Tutors": sortByNewtutors,
};

const applySorting = (
  filters: any,
  searchResults: any,
  requestData: any,
  searchObj: any,
) => {
  let sortFunction = sortingFunctions[filters.sortBy];
  if (sortFunction) {
    return sortFunction(searchResults, requestData, searchObj);
  }
  return sortingFunctions.Recommended(searchResults, requestData, searchObj);
};

export const applyFilters = (searchResults: any = [], filters: any = {}) => {
  if (filters.lessonType) {
    searchResults = searchResults.filter((tutor) =>
      tutor.delivery.includes(filters.lessonType),
    );
  }

  if (filters.gender.length > 0) {
    searchResults = searchResults.filter((tutor) =>
      filters.gender.includes(tutor.gender),
    );
  }

  if (filters.showPremium) {
    searchResults = searchResults.filter((tutor) => tutor.level === "premium");
  }

  if (filters.minPrice) {
    searchResults = searchResults.filter(
      (tutor) => tutor.lessonFee >= filters.minPrice,
    );
  }

  if (filters.maxPrice) {
    searchResults = searchResults.filter(
      (tutor) => tutor.lessonFee <= filters.maxPrice,
    );
  }

  if (filters.minExperience) {
    searchResults = searchResults.filter(
      (tutor) => tutor.experience >= filters.minExperience,
    );
  }

  if (filters.maxAge) {
    searchResults = searchResults.filter(
      (tutor) => filters.maxAge >= tutor.age,
    );
  }

  if (filters.minAge) {
    searchResults = searchResults.filter(
      (tutor) => filters.minAge <= tutor.age,
    );
  }

  if (filters.educationCountries.length > 0) {
    searchResults = searchResults.filter((tutor) =>
      filters.educationCountries.some((item) =>
        tutor.eduCountries.includes(item),
      ),
    );
  }

  if (filters.educationDegrees.length > 0) {
    searchResults = searchResults.filter((tutor) =>
      filters.educationDegrees.some((item) => tutor.eduDegrees.includes(item)),
    );
  }

  if (filters.educationGrades.length > 0) {
    searchResults = searchResults.filter((tutor) =>
      filters.educationGrades.some((item) => tutor.eduGrades.includes(item)),
    );
  }
  return searchResults;
};

export function categorizeTutors(tutorOptions = []) {
  const premiumTutors = tutorOptions.filter(
    (tutor) =>
      tutor.level === "premium" &&
      !tutor.isNewTutor &&
      tutor.rating >= 4.5 &&
      tutor.lessonsTaught > 0,
  );

  const highlyRatedTutors = tutorOptions.filter(
    (tutor) =>
      tutor.level !== "premium" &&
      tutor.rating >= 4.5 &&
      !tutor.isNewTutor &&
      tutor.lessonsTaught > 0,
  );
  const newlyApprovedTutors = tutorOptions.filter(
    (tutor) =>
      tutor.isNewTutor &&
      (tutor.lessonsTaught === 0 || !Boolean(tutor.lessonsTaught)) &&
      (tutor.rating === 0 || !Boolean(tutor.rating)),
  );
  const activatedNewTutors = tutorOptions.filter(
    (tutor) =>
      tutor.isNewTutor &&
      tutor.lessonsTaught > 0 &&
      (tutor.rating === 0 || !Boolean(tutor.rating)),
  );
  const highlyRatedNewTutors = tutorOptions.filter(
    (tutor) =>
      tutor.isNewTutor && tutor.lessonsTaught > 0 && tutor.rating >= 4.5,
  );
  const unactivatedTutors = tutorOptions.filter(
    (tutor) =>
      !tutor.isNewTutor &&
      tutor.level !== "premium" &&
      (tutor.lessonsTaught === 0 || !Boolean(tutor.lessonsTaught)) &&
      (tutor.rating === 0 || !Boolean(tutor.rating)),
  );
  const averagelyRatedTutors = tutorOptions.filter(
    (tutor) =>
      tutor.level !== "premium" &&
      tutor.lessonsTaught > 0 &&
      tutor.rating > 3.5 &&
      tutor.rating < 4.5,
  );
  const activatedUnratedTutors = tutorOptions.filter(
    (tutor) =>
      !tutor.isNewTutor &&
      tutor.lessonsTaught > 0 &&
      (tutor.rating === 0 || !Boolean(tutor.rating)),
  );
  const poorlyRatedTutors = tutorOptions.filter(
    (tutor) =>
      tutor.level !== "premium" &&
      tutor.lessonsTaught > 0 &&
      tutor.rating > 0 &&
      tutor.rating <= 3.5,
  );
  return {
    premiumTutors,
    highlyRatedTutors,
    newlyApprovedTutors,
    activatedNewTutors,
    highlyRatedNewTutors,
    unactivatedTutors,
    averagelyRatedTutors,
    activatedUnratedTutors,
    poorlyRatedTutors,
  };
}

/*
    This function generates the tutors who show on the
    first page of the search results  
*/

function getFirstPageTutors(tutorOptions = [], resultsPerPage) {
  let {
    premiumTutors,
    highlyRatedTutors,
    newlyApprovedTutors,
    activatedNewTutors,
    highlyRatedNewTutors,
    unactivatedTutors,
    averagelyRatedTutors,
    activatedUnratedTutors,
  } = categorizeTutors(tutorOptions);

  let _premiumTutors = premiumTutors.splice(0, 3);
  let premiumCount = _premiumTutors.length;
  let otherTutorsCount = resultsPerPage - premiumCount;

  let _highlyRatedNewTutors = highlyRatedNewTutors.splice(0, 2);
  let _newlyApprovedTutors = newlyApprovedTutors.splice(0, 2);
  let _activatedNewTutors = activatedNewTutors.splice(0, 1);
  let topNewTutors = _newlyApprovedTutors.concat(
    _highlyRatedNewTutors,
    _activatedNewTutors,
  );

  let __highlyRatedTutors = highlyRatedTutors.splice(0, 2);
  let slotsLeft =
    otherTutorsCount - __highlyRatedTutors.length - topNewTutors.length;
  let _highlyRatedTutors = highlyRatedTutors.splice(0, slotsLeft);

  let topFirstPageTutors = __highlyRatedTutors.concat(
    topNewTutors,
    _highlyRatedTutors,
  );

  //This section checks if the Result Page is complete. If not, adds more tutors.
  let combinedArrayResult = [topFirstPageTutors];

  let tutorArrays = [
    highlyRatedNewTutors,
    averagelyRatedTutors,
    unactivatedTutors,
    highlyRatedTutors,
    activatedNewTutors,
    activatedUnratedTutors,
    newlyApprovedTutors,
  ];

  //This calls the combineArray func with each tutorArray element in succession
  //adding the result of each function call to the next function call.

  for (let i = 0; i < tutorArrays.length; i++) {
    combinedArrayResult.push(
      combineSearchArrays(
        combinedArrayResult[i],
        tutorArrays[i],
        otherTutorsCount,
      ),
    );
  }

  let finalResult = combinedArrayResult.pop();

  // shuffle(firstPageTutors);

  let remainingTutors = premiumTutors.concat(
    highlyRatedTutors,
    newlyApprovedTutors,
    activatedNewTutors,
    highlyRatedNewTutors,
    unactivatedTutors,
    averagelyRatedTutors,
    activatedUnratedTutors,
  );

  return {
    firstPage: [..._premiumTutors, ...finalResult],
    remainingData: remainingTutors,
  };
}

export function getSearchPageResults(tutorOptions = [], resultsPerPage: any) {
  let {
    premiumTutors,
    highlyRatedTutors,
    newlyApprovedTutors,
    activatedNewTutors,
    highlyRatedNewTutors,
    unactivatedTutors,
    averagelyRatedTutors,
    activatedUnratedTutors,
  } = categorizeTutors(tutorOptions);

  let _premiumTutors = premiumTutors.splice(0, 3);

  let topPlusPremium =
    _premiumTutors.length === 0
      ? _premiumTutors.concat(highlyRatedNewTutors.splice(0, 3))
      : _premiumTutors.concat(highlyRatedNewTutors.splice(0, 2));

  let topRatedTutors =
    topPlusPremium.length === 0
      ? topPlusPremium.concat(highlyRatedTutors.splice(0, 4))
      : topPlusPremium.length === 1
      ? topPlusPremium.concat(highlyRatedTutors.splice(0, 3))
      : topPlusPremium.concat(highlyRatedTutors.splice(0, 2));

  let topPlusAverage =
    topRatedTutors.length === 2
      ? topRatedTutors.concat(averagelyRatedTutors.splice(0, 3))
      : topRatedTutors.length === 3
      ? topRatedTutors.concat(averagelyRatedTutors.splice(0, 2))
      : topRatedTutors.concat(averagelyRatedTutors.splice(0, 1));

  let topPlusNewlyApproved: any =
    topPlusAverage.length < 4
      ? topPlusAverage.concat(newlyApprovedTutors.splice(0, 3))
      : topPlusAverage.length === 4
      ? topPlusAverage.concat(newlyApprovedTutors.splice(0, 2))
      : topPlusAverage.concat(newlyApprovedTutors.splice(0, 1));

  let topPlusUnactivated =
    resultsPerPage - topPlusNewlyApproved > 1
      ? topPlusNewlyApproved.concat(
          activatedUnratedTutors.splice(0, 1),
          unactivatedTutors.splice(0, 1),
        )
      : topPlusNewlyApproved.concat(unactivatedTutors.splice(0, 1));

  //This section checks if the Result Page is complete. If not, adds more tutors.
  let combinedArrayResult = [topPlusUnactivated];

  let tutorArrays = [
    activatedNewTutors,
    activatedUnratedTutors,
    highlyRatedTutors,
    highlyRatedNewTutors,
    newlyApprovedTutors,
    averagelyRatedTutors,
    premiumTutors,
    unactivatedTutors,
  ];

  //This calls the combineArray func with each tutorArray element in succession
  //adding the result of each function call to the next function call.

  for (let i = 0; i < tutorArrays.length; i++) {
    combinedArrayResult.push(
      combineSearchArrays(
        combinedArrayResult[i],
        tutorArrays[i],
        resultsPerPage,
      ),
    );
  }

  let finalResult = combinedArrayResult.pop();

  // shuffle(finalResult);

  let newData = premiumTutors.concat(
    highlyRatedTutors,
    newlyApprovedTutors,
    activatedNewTutors,
    highlyRatedNewTutors,
    unactivatedTutors,
    averagelyRatedTutors,
    activatedUnratedTutors,
  );

  return {
    searchResult: finalResult,
    newRemainingData: newData,
  };
}

export function getPremiumTutors(tutorOptions = [], resultsPerPage) {
  let premium = categorizeTutors(tutorOptions).premiumTutors;

  return chunkArray(premium, resultsPerPage);
}

const transformBookings = (activeBookings = []) => {
  let allSchedule = [];
  let result = {};

  activeBookings.forEach((booking) => allSchedule.push(...booking.schedule));
  allSchedule.forEach((schedule) => {
    let day = schedule.split(":")[0].trim();
    let time = schedule.split(":")[1].trim();

    if (result[day]) {
      result = { ...result, [day]: [...result[day], time] };
    } else {
      result = { ...result, [day]: [time] };
    }
  });

  return result;
};

//This function generates the final search results
export function newGenerateSearchResults(
  uniqueResults = [],
  tutorDataOptions = [],
  resultsPerPage,
  requestData = {},
  searchObj = {},
  filters: any = {},
  skipFilter = false,
) {
  // let searchResults = getRelevanceScore(
  //   uniqueResults,
  //   tutorDataOptions,
  //   searchObj
  // );
  let searchResults = getRelevantScorePriceOnly(
    uniqueResults,
    tutorDataOptions,
  );
  let sortedResults = applySorting(
    filters,
    searchResults,
    requestData,
    searchObj,
  );
  let filteredResults = skipFilter
    ? sortedResults
    : applyFilters(sortedResults, filters);
  if (!skipFilter) {
    if (filters.sortBy === "Recommended" && !filters.showPremium) {
      let { firstPage, remainingData } = getFirstPageTutors(
        filteredResults,
        resultsPerPage,
      );
      let pageGroups = [];
      let continueLoop = true;
      while (continueLoop) {
        let { searchResult, newRemainingData } = getSearchPageResults(
          remainingData,
          resultsPerPage,
        );
        if (searchResult.length > 0) {
          pageGroups.push(searchResult);
        }
        remainingData = newRemainingData;

        if (newRemainingData.length === 0) {
          continueLoop = false;
        }
      }
      return [firstPage, ...pageGroups];
    }
  }

  return chunkArray(filteredResults, resultsPerPage);
}

// logic already moved to the backend.
export const removeExcludedTutors = (
  tutorIDs,
  deniedTutors,
  specialities,
  tutorOptions = [],
  searchObj,
  requestData,
  options: any = {},
) => {
  const removeSelectedTutors = (tutorDataArray) => {
    let excludeTutors = [...tutorIDs, ...deniedTutors];
    if (excludeTutors.length)
      return tutorDataArray.filter(
        (tutor) => !excludeTutors.includes(tutor.userId),
      );
    return tutorDataArray;
  };

  const filterBySpecialities = (tutorDataArray) => {
    if (specialities.length) {
      tutorDataArray.filter((tutor) =>
        tutor.specialities.some((speciality) =>
          specialities.includes(speciality),
        ),
      );
    }
    return tutorDataArray;
  };

  const removeReachedMaxDeclines = (tutorDataArray) =>
    tutorDataArray.filter((tutor) => tutor.requestsDeclined < 3);

  const removeIgnoredRequests = (tutorDataArray) =>
    tutorDataArray.filter((tutor) => tutor.requestsNotResponded === 0);

  const declinedAllRequests = (tutor) =>
    tutor.totalJobsAssigned > 1 &&
    tutor.requestsDeclined === tutor.totalJobsAssigned;
  const removeDeclinedAllRequests = (tutorDataArray) =>
    tutorDataArray.filter((tutor) => !declinedAllRequests(tutor));

  const hasMultiplePendingJobs = (tutor) => {
    const previousPendingJobs =
      tutor.totalJobsAssigned -
      (tutor.requestPending +
        tutor.requestsDeclined +
        tutor.requestsNotResponded +
        tutor.totalJobsAccepted);

    const activeJobs =
      previousPendingJobs >= 0
        ? tutor.requestPending
        : tutor.requestPending + previousPendingJobs;
    if (activeJobs > 2) return true;
    return false;
  };

  const removeMultiplePendingJobs = (tutorDataArray) =>
    tutorDataArray.filter((tutor) => tutor.requestPending < 4);
  // tutorDataArray.filter(tutor => !hasMultiplePendingJobs(tutor));

  const removeOutdatedCalendar = (tutorDataArray) =>
    tutorDataArray.filter(
      (tutor) =>
        diffInDays(new Date(), new Date(tutor.lastCalendarUpdate)) < 40,
    );

  const removeMaxDays = (tutorDataArray) =>
    tutorDataArray.filter(
      (tutor) => tutor.maxDays >= searchObj.lessonDays.length,
    );

  const removeMaxStudents = (tutorDataArray) =>
    tutorDataArray.filter(
      (tutor) => tutor.maxStudents >= searchObj.names.length,
    );

  const removeMaxHours = (tutorDataArray) => {
    let hours = requestData.lessonDetails.lessonSchedule.lessonHours;
    return tutorDataArray.filter((tutor) => tutor.maxHours >= hours);
  };

  const removeFullyBookedTutors = (tutorDataArray) => {
    function maxBookings(tutor) {
      if (tutor.maxDays <= 2) return 1;
      if (tutor.maxDays > 2 && tutor.maxDays <= 6) return tutor.maxDays - 1;
      return 5;
    }

    return tutorDataArray.filter(
      (tutor) => tutor.activeBookings.length <= maxBookings(tutor),
    );
  };

  const removeUnavailableTutors = (tutorDataArray) => {
    const lessonDays = searchObj.lessonDays;

    function hasMatchingDays(availability) {
      const tutorsDays = Object.keys(availability);
      return lessonDays.every((day) => tutorsDays.includes(day));
    }

    return tutorDataArray.filter((tutor) =>
      hasMatchingDays(tutor.availability),
    );
  };

  const removeUnavailableTimes = (tutorDataArray) => {
    const { mapPeriodToTime } = SCHEDULE_DATA;
    const lessonDays = searchObj.lessonDays;
    let lessonTime = requestData.lessonDetails.lessonSchedule.lessonTime;
    let selectedPeriod;

    Object.entries(mapPeriodToTime).forEach(([period, times]) => {
      if (times.includes(lessonTime)) selectedPeriod = period;
    });

    let result = tutorDataArray.filter((tutor) =>
      lessonDays.every((day) =>
        tutor.availability[day].includes(selectedPeriod),
      ),
    );

    return result;
  };

  const removeTutorsWithSameBookings = (tutorDataArray) => {
    const lessonDays = searchObj.lessonDays;

    let newData = tutorDataArray.filter((tutor) => {
      let availableDays = 0;
      if (tutor.activeBookings.length) {
        let bookings = transformBookings(tutor.activeBookings);

        lessonDays.forEach((day) => {
          let classesPerDay = bookings[day] ? bookings[day].length : 0;
          if (classesPerDay < 2) availableDays++;
        });

        if (lessonDays.length < 3) return lessonDays.length === availableDays;
        if (lessonDays.length >= 3)
          return lessonDays.length - availableDays <= 1;
      } else return true;
    });

    return newData;
  };
  let funcs = [removeSelectedTutors];
  if (options.filterBySpecialities) {
    funcs.push(filterBySpecialities);
  }
  if (options.removeReachedMaxDeclines) {
    funcs.push(removeReachedMaxDeclines);
  }
  if (options.removeIgnoredRequests) {
    funcs.push(removeIgnoredRequests);
  }
  if (options.removeDeclinedAllRequests) {
    funcs.push(removeDeclinedAllRequests);
  }
  if (options.removeMultiplePendingJobs) {
    funcs.push(removeMultiplePendingJobs);
  }
  if (options.removeOutdatedCalendar) {
    funcs.push(removeOutdatedCalendar);
  }
  if (options.removeMaxDays) {
    funcs.push(removeMaxDays);
  }
  if (options.removeMaxStudents) {
    funcs.push(removeMaxStudents);
  }
  if (options.removeMaxHours) {
    funcs.push(removeMaxHours);
  }
  if (options.removeUnavailableTutors) {
    funcs.push(removeUnavailableTutors);
  }
  if (options.removeUnavailableTimes) {
    funcs.push(removeUnavailableTimes);
  }
  if (options.removeFullyBookedTutors) {
    funcs.push(removeFullyBookedTutors);
  }
  if (options.removeTutorsWithSameBookings) {
    funcs.push(removeTutorsWithSameBookings);
  }
  return pipeFunc(...funcs)(tutorOptions);
};

export function trimSearchResult(
  tutorOptions = [],
  selectedTutorIDs = [],
  requestData = {},
  searchObj = {},
  specialities = [],
  deniedTutors = [],
) {
  let uniqueResults = removeExcludedTutors(
    selectedTutorIDs,
    deniedTutors,
    specialities,
    tutorOptions,
    searchObj,
    requestData,
  );
  let tutorDataOptions = uniqueResults.map((tutor) => {
    return {
      ...tutor,
      otherDetails: interpretTutorData(tutor, requestData, searchObj),
    };
  });
  return getRelevantScoreExcludingPrice(
    tutorDataOptions,
    tutorDataOptions.map((o) => ({
      ...o.otherDetails,
      userId: o.userId,
    })),
    searchObj,
  );
  return uniqueResults;
}
